import { useMemo } from "react";

import { InclusionFilterStatus, OutboxTab, PriorityFilterStatus } from "@/enum/list.enum";
import { useGetOutboxCountsQuery } from "@/pages/Private/redux/list/list-outbox.api";

import { SortParams } from "@/types/types";

import { DropdownWithCheckboxesItem } from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import { IFilterWithValues } from "../../../List/components/FilterModal";
import { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";
import { ALL_SENDERS_ID } from "./outboxSenderId";
import { OutboxCounts } from "../../../List/schema/outbox-counts";
import {
	buildOutboxRequestScope,
	OutboxSorting,
	OutboxStatusTab,
	resolveOutboxSort,
} from "./outboxStatusTabs";

interface UseOutboxTabScopeArgs {
	statusTab: OutboxStatusTab;
	allInclude: OutboxStatusTab[];
	/** Current query string — an explicit ?sortBy overrides the per-tab default sort. */
	locationSearch: string;
	sortingInbox: SortParams | undefined;
	selectedSenders: DropdownWithCheckboxesItem[];
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	query: string;
	filter: IFilterWithValues | undefined;
	campaignIds: string[];
	/** Chat/campaign source scope; undefined = no narrowing. */
	outboxSource: "chat" | "campaign" | undefined;
	/** Counts wait for the sending users, like the list fetch. */
	skip: boolean;
}

interface OutboxTabScope {
	outboxTabsScope: OutboxTab[];
	resolvedSorting: SortParams | undefined;
	senderIds: number[];
	outboxCounts: OutboxCounts | undefined;
}

/**
 * The Outbox status-tab request scope (PREC-3850): maps the active tab (and the
 * All tab's include-set) onto the `outboxTabs` param, resolves the per-tab sort
 * default, and fetches the tab badges over the same filter set minus the scope.
 */
export const useOutboxTabScope = ({
	statusTab,
	allInclude,
	locationSearch,
	sortingInbox,
	selectedSenders,
	selectedLists,
	query,
	filter,
	campaignIds,
	outboxSource,
	skip,
}: UseOutboxTabScopeArgs): OutboxTabScope => {
	const outboxTabsScope = useMemo(
		() => buildOutboxRequestScope(statusTab, allInclude).outboxTabs,
		[statusTab, allInclude]
	);

	const urlHasExplicitSort = new URLSearchParams(locationSearch).has("sortBy");

	const resolvedSorting = useMemo(
		() =>
			resolveOutboxSort(
				statusTab,
				urlHasExplicitSort,
				sortingInbox as OutboxSorting | undefined
			) as SortParams | undefined,
		[statusTab, urlHasExplicitSort, sortingInbox]
	);

	const senderIds = useMemo(
		() =>
			selectedSenders
				.filter((sender) => sender.id !== ALL_SENDERS_ID)
				.map((sender) => sender.id as number),
		[selectedSenders]
	);

	const { data: outboxCounts } = useGetOutboxCountsQuery(
		{
			page: 1,
			limit: 1,
			searchValue: query,
			filter: !query ? filter : undefined,
			senders: !query ? senderIds : [],
			listIds:
				selectedLists?.length > 0 && !query
					? selectedLists.map((list) => list.id as number)
					: undefined,
			campaignIds: !query && campaignIds.length > 0 ? campaignIds : undefined,
			outboxSource: !query ? outboxSource : undefined,
			inclusionFilterStatus: [InclusionFilterStatus.ADDED],
			priorityFilterStatus: [PriorityFilterStatus.ALL],
		},
		{ skip }
	);

	return { outboxTabsScope, resolvedSorting, senderIds, outboxCounts };
};
